/**
 * Sign-in and provisioning for a tenant (roadmap I-4; migration 986).
 *
 * Second factor: a tenant administrator can require everyone in the tenant to
 * sign in with an authenticator app code (Supabase Auth TOTP). The database
 * enforces it — a session without the code sees nothing of that tenant's
 * workspaces — so the app asks right after sign-in, before anything loads.
 *
 * Provisioning: an identity provider (Okta, Entra ID) adds and removes people
 * in a workspace through the `scim` edge function, with a provisioning token
 * an administrator makes here.
 */

import { getSupabaseClient } from './supabaseClient';

// ── Second factor ─────────────────────────────────────────────

export interface SecondFactorRequirement {
  /** True when any of this person's tenants requires a second factor. */
  required: boolean;
  /** The tenants that require it, by name. */
  tenants: string[];
  /** The session's level now: aal2 means it was signed in with a code. */
  level: 'aal1' | 'aal2';
}

export async function fetchSecondFactorRequirement(): Promise<SecondFactorRequirement> {
  const { data, error } = await getSupabaseClient().rpc('my_second_factor_requirement');
  if (error) throw error;
  const r = (data ?? {}) as Partial<SecondFactorRequirement>;
  return { required: !!r.required, tenants: r.tenants ?? [], level: r.level === 'aal2' ? 'aal2' : 'aal1' };
}

/** The person's authenticator app, when they have set one up. */
export async function verifiedAuthenticator(): Promise<{ id: string } | null> {
  const { data, error } = await getSupabaseClient().auth.mfa.listFactors();
  if (error) throw error;
  const factor = data?.totp?.find((f) => f.status === 'verified');
  return factor ? { id: factor.id } : null;
}

export interface AuthenticatorEnrollment {
  factorId: string;
  /** An SVG image of the QR code, as a data URL. */
  qrCode: string;
  /** The same secret, for typing into the app by hand. */
  secret: string;
}

/** Starts setting up an authenticator app. Clears any half-finished setup first. */
export async function startAuthenticatorEnrollment(): Promise<AuthenticatorEnrollment> {
  const auth = getSupabaseClient().auth;
  const { data: list } = await auth.mfa.listFactors();
  for (const f of list?.all ?? []) {
    if (f.factor_type === 'totp' && f.status === 'unverified') await auth.mfa.unenroll({ factorId: f.id });
  }
  const { data, error } = await auth.mfa.enroll({ factorType: 'totp', friendlyName: `Authenticator ${new Date().toISOString().slice(0, 10)}` });
  if (error) throw error;
  return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
}

/** Checks a six-digit code; on success the session is signed in with a second factor. */
export async function verifyAuthenticatorCode(factorId: string, code: string): Promise<void> {
  const { error } = await getSupabaseClient().auth.mfa.challengeAndVerify({ factorId, code: code.replace(/\s+/g, '') });
  if (error) {
    throw new Error(/invalid|expired/i.test(error.message) ? 'That code did not match. Check the app and enter the newest code.' : error.message);
  }
}

export interface TenantSecondFactor {
  tenantId: string;
  required: boolean;
  /** This person may turn it on or off. */
  canChange: boolean;
}

export async function fetchTenantSecondFactor(tenantId: string, userId: string, isPlatformAdmin: boolean): Promise<TenantSecondFactor> {
  const supabase = getSupabaseClient();
  const [{ data: setting }, { data: admin }] = await Promise.all([
    supabase.from('tenant_security_settings').select('require_second_factor').eq('tenant_id', tenantId).maybeSingle(),
    supabase.from('tenant_memberships').select('id').eq('tenant_id', tenantId).eq('user_id', userId).eq('role', 'admin').maybeSingle(),
  ]);
  return { tenantId, required: !!setting?.require_second_factor, canChange: isPlatformAdmin || !!admin };
}

export async function setTenantSecondFactor(tenantId: string, required: boolean): Promise<void> {
  const { data, error } = await getSupabaseClient().rpc('set_tenant_second_factor', { p_tenant_id: tenantId, p_required: required });
  if (error) throw error;
  const r = data as { success?: boolean; message?: string } | null;
  if (!r?.success) throw new Error(r?.message ?? 'Could not change the requirement.');
}

// ── Provisioning (SCIM) ───────────────────────────────────────

/** The base URL an identity provider is given. */
export function scimBaseUrl(): string {
  return `${import.meta.env.VITE_SUPABASE_URL ?? ''}/functions/v1/scim/v2`;
}

export interface ScimToken {
  id: string;
  hint: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface ProvisionedPerson {
  userId: string;
  userName: string;
  displayName: string;
  active: boolean;
  updatedAt: string;
}

export interface IdentityEvent {
  id: string;
  kind: string;
  ok: boolean;
  detail: string | null;
  subjectEmail: string | null;
  createdAt: string;
}

export async function listScimTokens(workspaceId: string): Promise<ScimToken[]> {
  const { data, error } = await getSupabaseClient()
    .from('scim_tokens')
    .select('id, token_hint, label, created_at, last_used_at, revoked_at')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((t) => ({
    id: t.id, hint: t.token_hint, label: t.label, createdAt: t.created_at, lastUsedAt: t.last_used_at, revokedAt: t.revoked_at,
  }));
}

/** A new token, shown once: only its hash is kept. */
export async function createScimToken(workspaceId: string, label: string): Promise<string> {
  const { data, error } = await getSupabaseClient().rpc('create_scim_token', { p_workspace_id: workspaceId, p_label: label });
  if (error) throw error;
  const r = data as { success?: boolean; token?: string; message?: string } | null;
  if (!r?.success || !r.token) throw new Error(r?.message ?? 'Could not create a token.');
  return r.token;
}

export async function revokeScimToken(tokenId: string): Promise<void> {
  const { data, error } = await getSupabaseClient().rpc('revoke_scim_token', { p_token_id: tokenId });
  if (error) throw error;
  const r = data as { success?: boolean; message?: string } | null;
  if (!r?.success) throw new Error(r?.message ?? 'Could not revoke the token.');
}

export async function listProvisionedPeople(workspaceId: string): Promise<ProvisionedPerson[]> {
  const { data, error } = await getSupabaseClient()
    .from('scim_users')
    .select('user_id, user_name, display_name, given_name, family_name, active, updated_at')
    .eq('workspace_id', workspaceId)
    .order('user_name');
  if (error) throw error;
  return (data ?? []).map((p) => ({
    userId: p.user_id,
    userName: p.user_name,
    displayName: p.display_name ?? ([p.given_name, p.family_name].filter(Boolean).join(' ') || p.user_name),
    active: p.active,
    updatedAt: p.updated_at,
  }));
}

/** The identity log for a workspace and its tenant, newest first. */
export async function listIdentityEvents(workspaceId: string, tenantId: string | null, limit = 50): Promise<IdentityEvent[]> {
  let q = getSupabaseClient()
    .from('identity_events')
    .select('id, kind, ok, detail, subject_email, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);
  q = tenantId ? q.or(`workspace_id.eq.${workspaceId},and(tenant_id.eq.${tenantId},workspace_id.is.null)`) : q.eq('workspace_id', workspaceId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []).map((e) => ({ id: e.id, kind: e.kind, ok: e.ok, detail: e.detail, subjectEmail: e.subject_email, createdAt: e.created_at }));
}
